import { PALETTE } from "../voxelColor";
import type { VoxelData } from "../voxelTypes";

/**
 * The community project, standing in the village.
 *
 * The Town Square existed only as a progress ring in a side panel, which made the one thing the
 * whole guild builds together the one thing nobody could see. It is built here instead, around
 * the well that was already at the centre of the authored village, and it changes shape as the
 * project passes each of its five stages.
 *
 * Every stage is cumulative: stage three is stage two with more standing in it, so the square
 * only ever gains. Nothing here is loaded from a file — same grid-snapped blocks, vertex colours
 * and merged buffers as the cottages.
 */

/** Where the square is built, in world voxel coordinates. The authored well sits here. */
export const TOWN_SQUARE_CENTER = { x: 2, z: 2 };
/** How far the paving reaches. The well keeps the middle, so the ring starts outside it. */
const PLAZA_RADIUS = 11;
const WELL_CLEARANCE = 3.5;

/** Positions evenly spaced around a ring, starting at a fixed angle so they never shuffle. */
function ring(count: number, radius: number, phase = 0): Array<{ x: number; z: number }> {
  return Array.from({ length: count }, (_, index) => {
    const angle = phase + (index / count) * Math.PI * 2;
    return { x: Math.round(Math.cos(angle) * radius), z: Math.round(Math.sin(angle) * radius) };
  });
}

/** A stack of voxels at one spot, from `fromY` upward. */
function column(out: VoxelData[], x: number, z: number, fromY: number, heights: Array<{ color: number; scale?: number }>) {
  heights.forEach((entry, index) => {
    out.push({ x, y: fromY + index, z, color: entry.color, materialType: "opaque", ...(entry.scale ? { scale: entry.scale } : {}) });
  });
}

/** Marker stakes and material piles — the square before anything has been built. */
function survey(out: VoxelData[], groundY: number) {
  for (const spot of ring(8, PLAZA_RADIUS - 1)) {
    column(out, spot.x, spot.z, groundY, [
      { color: PALETTE.WOOD_OAK_MEDIUM, scale: 0.3 },
      { color: PALETTE.WOOD_OAK_MEDIUM, scale: 0.3 },
      { color: PALETTE.FLOWER_RED, scale: 0.35 }
    ]);
  }
  // Piles of stone waiting to be laid, low and irregular so they read as material rather than wall.
  for (const spot of ring(3, PLAZA_RADIUS - 4, 0.6)) {
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dz = -1; dz <= 1; dz += 1) {
        const height = Math.abs(dx) + Math.abs(dz) === 0 ? 2 : 1;
        for (let y = 0; y < height; y += 1) {
          out.push({ x: spot.x + dx, y: groundY + y, z: spot.z + dz, color: y === 0 ? PALETTE.STONE_MEDIUM : PALETTE.STONE_LIGHT, materialType: "opaque" });
        }
      }
    }
  }
}

/** Cobbled paving and the four approaches into it. */
function foundation(out: VoxelData[], groundY: number) {
  for (let x = -PLAZA_RADIUS; x <= PLAZA_RADIUS; x += 1) {
    for (let z = -PLAZA_RADIUS; z <= PLAZA_RADIUS; z += 1) {
      const distance = Math.hypot(x, z);
      if (distance > PLAZA_RADIUS || distance < WELL_CLEARANCE) continue;
      // Two cobble tones alternating on a checker, which is what stops a large flat area of one
      // colour reading as a painted disc rather than as laid stone.
      const cobble = (Math.abs(x) + Math.abs(z)) % 2 === 0 ? PALETTE.COBBLE : PALETTE.STONE_MEDIUM;
      out.push({ x, y: groundY, z, color: distance > PLAZA_RADIUS - 1.2 ? PALETTE.STONE_DARK : cobble, materialType: "opaque" });
    }
  }
  // Four approaches reaching out past the paving to meet the lanes.
  for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
    for (let step = PLAZA_RADIUS; step <= PLAZA_RADIUS + 4; step += 1) {
      for (let side = -1; side <= 1; side += 1) {
        out.push({
          x: dx * step + dz * side,
          y: groundY,
          z: dz * step + dx * side,
          color: PALETTE.PATH_SAND,
          materialType: "opaque"
        });
      }
    }
  }
}

/** The notice board and the benches around the edge. */
function gatheringPlace(out: VoxelData[], groundY: number) {
  // Notice board, on the north side where the approach arrives.
  const boardZ = -(PLAZA_RADIUS - 3);
  for (const postX of [-2, 2]) {
    column(out, postX, boardZ, groundY + 1, [
      { color: PALETTE.WOOD_OAK_DARK, scale: 0.4 },
      { color: PALETTE.WOOD_OAK_DARK, scale: 0.4 },
      { color: PALETTE.WOOD_OAK_DARK, scale: 0.4 }
    ]);
  }
  for (let x = -2; x <= 2; x += 1) {
    for (let y = 2; y <= 3; y += 1) {
      out.push({ x, y: groundY + y, z: boardZ, color: PALETTE.WOOD_OAK_LIGHT, materialType: "opaque", scaleZ: 0.3 });
    }
  }
  out.push({ x: 0, y: groundY + 4, z: boardZ, color: PALETTE.THATCH_DARK, materialType: "opaque", scaleY: 0.4, scaleZ: 0.5 });

  // Benches, set back from the paving edge and facing the middle.
  for (const spot of ring(4, PLAZA_RADIUS - 3, Math.PI / 4)) {
    const alongX = Math.abs(spot.x) < Math.abs(spot.z);
    for (let offset = -1; offset <= 1; offset += 1) {
      const x = spot.x + (alongX ? offset : 0);
      const z = spot.z + (alongX ? 0 : offset);
      out.push({ x, y: groundY + 1, z, color: PALETTE.WOOD_OAK_MEDIUM, materialType: "opaque", scaleY: 0.35 });
      out.push({ x, y: groundY + 2, z: z + (alongX ? 0.4 : 0), color: PALETTE.WOOD_OAK_DARK, materialType: "opaque", scaleY: 0.5, scaleZ: alongX ? 0.3 : 1, scaleX: alongX ? 1 : 0.3 });
    }
  }
}

/** Lamps, planters and the trees that make it somewhere to stand rather than somewhere to cross. */
function finishingTouches(out: VoxelData[], groundY: number) {
  for (const spot of ring(6, PLAZA_RADIUS - 1.5, Math.PI / 6)) {
    column(out, spot.x, spot.z, groundY + 1, [
      { color: PALETTE.STONE_DARK, scale: 0.45 },
      { color: PALETTE.WOOD_OAK_DARK, scale: 0.3 },
      { color: PALETTE.WOOD_OAK_DARK, scale: 0.3 }
    ]);
    out.push({ x: spot.x, y: groundY + 4, z: spot.z, color: PALETTE.LAMP_GOLD, materialType: "emissive", scale: 0.55 });
    out.push({ x: spot.x, y: groundY + 5, z: spot.z, color: PALETTE.STONE_DARK, materialType: "opaque", scale: 0.4 });
  }
  // Planters, in the gaps the lamps leave.
  for (const spot of ring(4, PLAZA_RADIUS - 5, Math.PI / 2 + 0.4)) {
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dz = -1; dz <= 1; dz += 1) {
        const rim = Math.abs(dx) === 1 || Math.abs(dz) === 1;
        out.push({ x: spot.x + dx, y: groundY + 1, z: spot.z + dz, color: rim ? PALETTE.STONE_LIGHT : PALETTE.DIRT_DARK, materialType: "opaque", scaleY: 0.6 });
        if (!rim) continue;
      }
    }
    out.push({ x: spot.x, y: groundY + 2, z: spot.z, color: PALETTE.LEAF_OAK_LUSH, materialType: "foliage", scale: 0.8 });
    out.push({ x: spot.x, y: groundY + 3, z: spot.z, color: PALETTE.FLOWER_YELLOW, materialType: "foliage", scale: 0.5 });
  }
}

/** Bunting between the lamps, which is what an open square looks like on the day it opens. */
function open(out: VoxelData[], groundY: number) {
  const lamps = ring(6, PLAZA_RADIUS - 1.5, Math.PI / 6);
  lamps.forEach((lamp, index) => {
    const next = lamps[(index + 1) % lamps.length]!;
    const colors = [PALETTE.FLOWER_RED, PALETTE.FLOWER_YELLOW, PALETTE.FLOWER_BLUE, PALETTE.FLOWER_WHITE];
    for (let step = 1; step < 6; step += 1) {
      const t = step / 6;
      // Dipped in the middle, the way a line strung between two poles actually hangs.
      const sag = Math.sin(t * Math.PI) * 1.2;
      out.push({
        x: lamp.x + (next.x - lamp.x) * t,
        y: groundY + 5 - sag,
        z: lamp.z + (next.z - lamp.z) * t,
        color: colors[step % colors.length]!,
        materialType: "opaque",
        scale: 0.35
      });
    }
  });
  // A banner over the northern approach, so the square announces itself from the lane.
  for (let x = -3; x <= 3; x += 1) {
    out.push({ x, y: groundY + 6, z: -(PLAZA_RADIUS + 1), color: PALETTE.THATCH_YELLOW, materialType: "opaque", scaleY: 0.5, scaleZ: 0.3 });
  }
}

const STAGE_BUILDERS = [survey, foundation, gatheringPlace, finishingTouches, open];

/**
 * The Town Square at a given stage, in world voxel coordinates.
 *
 * @param stage 0 to 4, matching `projectStageIndex` — survey, foundation, gathering place,
 * finishing touches, open.
 * @param groundAt The village's ground height at a world position, so the paving sits on the
 * terrain rather than through it.
 */
export function buildTownSquareVoxels(stage: number, groundAt: (x: number, z: number) => number): VoxelData[] {
  const local: VoxelData[] = [];
  const groundY = Math.round(groundAt(TOWN_SQUARE_CENTER.x, TOWN_SQUARE_CENTER.z));
  const reached = Math.max(0, Math.min(STAGE_BUILDERS.length - 1, Math.round(stage)));

  // Survey markers are what the square looks like *before* work starts, so they come down once
  // the paving goes in rather than staying under everyone's feet.
  if (reached === 0) survey(local, groundY);
  else for (let index = 1; index <= reached; index += 1) STAGE_BUILDERS[index]!(local, groundY);

  return local.map((voxel) => ({ ...voxel, x: voxel.x + TOWN_SQUARE_CENTER.x, z: voxel.z + TOWN_SQUARE_CENTER.z }));
}
